import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial } from "@/components/HubEditorial";
import { LocalLinks } from "@/components/LocalLinks";
import { norrbottenSections } from "@/data/lokala-djup";

const URL_SELF = "https://www.akhusbilfritid.se/husbil-norrbotten";

const FAQ = [
  {
    q: "Vilken är Norrbottens husbilshandlare?",
    a: "AK Husbil & Fritid i Kalix är en av Norrbottens familjeägda husbilshandlare. Vi har funnits sedan 2004 och säljer kontrollerade husbilar och husvagnar till kunder i hela regionen.",
  },
  {
    q: "Var i Norrbotten finns ni?",
    a: "Vi finns på Stabsvägen 5 i Kalix — centralt i Norrbotten och lätt att nå från Luleå, Boden, Piteå, Haparanda och Övertorneå.",
  },
  {
    q: "Kan ni leverera till Luleå eller andra orter i Norrbotten?",
    a: "Ja, vi hjälper till med transport av husbilen till din hemort. Ring så tar vi fram en lösning.",
  },
  {
    q: "Är husbilarna vinteranpassade?",
    a: "Vi säljer många husbilar som klarar norrländska vintrar. Fråga oss vad du behöver tänka på — vi vet vad som fungerar när kvicksilvret sjunker.",
  },
];

const PRIMARY_ORTER = [
  { name: "Kalix", to: "/husbil-kalix" as const },
  { name: "Luleå", to: "/husbil-lulea" as const },
  { name: "Boden", to: "/husbil-boden" as const },
  { name: "Piteå", to: "/husbil-pitea" as const },
  { name: "Haparanda", to: "/husbil-haparanda" as const },
];
const OTHER_ORTER = ["Övertorneå", "Överkalix", "Kiruna", "Gällivare", "Älvsbyn", "Arjeplog", "Jokkmokk"];

export const Route = createFileRoute("/husbil-norrbotten")({
  head: () => ({
    meta: [
      { title: "Husbilar Norrbotten – lager i Kalix, hemleverans" },
      {
        name: "description",
        content:
          "Husbilar och husvagnar till hela Norrbotten – Luleå, Boden, Piteå, Haparanda. Fukttestade och gasoltestade, transport ordnas. Familjeägt i Kalix sedan 2004.",
      },
      { name: "keywords", content: "husbil Norrbotten, husvagn Norrbotten, husbil Luleå, husbil Boden, husbil Piteå, husbilshandlare Norrbotten" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Husbil Norrbotten – AK Husbil & Fritid, Kalix" },
      { property: "og:description", content: "Kontrollerade husbilar till hela Norrbotten. Familjeägt sedan 2004." },
      { property: "og:url", content: URL_SELF },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Husbil Norrbotten", item: URL_SELF },
          ],
        }),
      },
    ],
  }),
  component: NorrbottenPage,
});

function NorrbottenPage() {
  return (
    <>
      <PageHeader
        label="Norrbotten · Husbil & husvagn"
        title={<>Husbil i <span className="font-italic-display text-accent">Norrbotten</span>.</>}
        sub="Vi säljer kontrollerade husbilar och husvagnar till kunder i hela Norrbotten — från Kalix i öst till Kiruna i norr. Familjeägt sedan 2004, med kunskapen om vad som fungerar i norrländskt klimat."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <Link to="/fordon" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Se husbilar i lager <ArrowRight size={14} />
          </Link>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
        </div>
      </section>

      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow">Norrländska husbilar</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Byggda för norrländska förhållanden.</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              I Norrbotten ställs andra krav på en husbil än söderöver. Isolering, värmesystem, batterikapacitet och däck som klarar snö och is — vi tänker på det. Vi säljer bara husbilar vi själva skulle använda i regionen.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Vinteranpassade husbilar med bra isolering",
                "Rådgivning om Truma, Alde och andra värmesystem",
                "Reservdelar och service från Kalix",
                "Vi levererar till hela Norrbotten",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <MapPin className="text-accent" size={32} />
            <h3 className="mt-6 font-display text-3xl">Vi hjälper kunder i:</h3>
            <div className="mt-6 flex flex-wrap gap-2">
              {PRIMARY_ORTER.map((ort) => (
                <Link
                  key={ort.name}
                  to={ort.to}
                  className="rounded-full border border-accent/40 bg-accent/5 px-4 py-2 text-sm font-medium transition hover:border-accent"
                >
                  Husbilar {ort.name}
                </Link>
              ))}
              {OTHER_ORTER.map((ort) => (
                <span key={ort} className="rounded-full border border-border bg-warm px-4 py-2 text-sm font-medium">{ort}</span>
              ))}
            </div>
            <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
              Bor du någon annanstans i Norrbotten? Ring oss ändå — vi ordnar oftast en lösning.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Söker du husvagn i stället? Se{" "}
              <Link to="/husvagn-kalix" className="font-medium text-accent underline underline-offset-4">
                husvagnar i Kalix — lager, visning och inbyte
              </Link>{" "}
              eller hela{" "}
              <Link to="/husvagnar-till-salu" className="font-medium text-accent underline underline-offset-4">
                husvagnslagret till salu
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">
            Husbil i <span className="font-italic-display text-accent">Norrbotten</span>.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="pt-16 md:pt-20">
        <HubEditorial eyebrow="Lokal fördjupning" sections={norrbottenSections} />
      </div>

      <LocalLinks
        keyId="husbil-norrbotten"
        text="husbil norrbotten vinter isolering klass III alde truma batteri litium besiktning vinterförvaring avställning"
        currentHref="/husbil-norrbotten"
      />
    </>
  );
}
